import React, { useState } from 'react';
import { FaEnvelope, FaCheck, FaTimes } from 'react-icons/fa';
import { submitToolLead } from '../../api/client';

const DISMISSED_KEY = 'tools_lead_prompt_dismissed';
const SUBMITTED_KEY = 'tools_lead_submitted';

/**
 * The optional counterpart to the old lead-capture gate.
 *
 * The tools used to be locked behind first name, last name and email before a
 * visitor saw anything at all. That is now gone: the tool runs first, and this
 * sits underneath it asking only for an email, only once the visitor has had
 * something of value.
 *
 * The wording promises exactly what the platform actually does with a ToolLead
 * row — a heads-up when new tools ship. It does not promise emailed results or
 * automated condition alerts, because nothing sends those.
 */
const ToolLeadPrompt = ({ toolId, toolName, user }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === 'true' ||
        localStorage.getItem(SUBMITTED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Nothing to ask a signed-in user for, and nothing to ask twice.
  if (user?.email || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // private mode — the prompt simply returns next visit
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Enter a valid email address');
      return;
    }
    setError('');
    setStatus('saving');
    try {
      await submitToolLead({
        // Name is optional now — the endpoint accepts an email on its own.
        first_name: firstName.trim(),
        last_name: '',
        email: value.toLowerCase(),
        tool_id: toolId,
      });
      try {
        localStorage.setItem(SUBMITTED_KEY, 'true');
      } catch {
        // ignore
      }
      setStatus('done');
    } catch {
      setStatus('error');
      setError('Could not save that right now. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-5 flex items-center gap-3">
        <FaCheck className="text-emerald-600 flex-shrink-0" />
        <p className="text-sm text-emerald-800">
          Thanks — we'll let you know when new tools land. Every tool here stays free to use.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-10 relative rounded-2xl border border-neutral-200 bg-white px-6 py-5 shadow-sm">
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-3 w-8 h-8 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors flex items-center justify-center"
      >
        <FaTimes className="text-sm" />
      </button>

      <h3 className="font-bold text-neutral-900 mb-1 pr-8">
        Want to hear when we add more tools?
      </h3>
      <p className="text-sm text-neutral-600 mb-4">
        {toolName ? `${toolName} is` : 'This tool is'} free and always will be — no account needed.
        Leave an email and we'll tell you when the next one ships.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-start gap-2">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name (optional)"
          aria-label="First name, optional"
          className="px-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 w-full sm:w-44"
        />
        <div className="flex-1 min-w-[12rem]">
          <div className="relative">
            <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
        </div>
        <button
          type="submit"
          disabled={status === 'saving'}
          className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
        >
          {status === 'saving' ? 'Saving…' : 'Keep me posted'}
        </button>
      </form>
    </div>
  );
};

export default ToolLeadPrompt;
